import { useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'
import { bindAppointmentAvailability } from './availability'

type AppointmentResponse = {
  ok?: boolean
  appointmentId?: string
  status?: string
  emailQueued?: boolean
  error?: string
}

function projectIdFromForm(form: HTMLFormElement) {
  const select = form.elements.namedItem('project') as HTMLSelectElement | null
  return select?.selectedOptions[0]?.dataset.projectId || null
}

export function PublicAppointmentBridge({ ready }: { ready: boolean }) {
  useEffect(() => {
    const client = supabase
    if (!ready || !client) return

    const form = document.getElementById('appointmentForm') as HTMLFormElement | null
    const submit = document.getElementById('appointmentSubmitButton') as HTMLButtonElement | null
    const status = document.getElementById('appointmentRequestStatus') as HTMLElement | null
    if (!form || !submit || !status || form.dataset.appointmentBound === 'true') return

    form.dataset.appointmentBound = 'true'
    const projectSelect = form.elements.namedItem('project') as HTMLSelectElement | null
    const availability = bindAppointmentAvailability(form, () => projectIdFromForm(form), message => setStatus('error', message))
    const projectChanged = () => { void availability.refresh() }
    projectSelect?.addEventListener('change', projectChanged)
    void availability.refresh()

    const setStatus = (kind: 'success' | 'error', message: string) => {
      status.hidden = false
      status.className = kind === 'success' ? 'notice notice--success' : 'notice notice--warning'
      status.innerHTML = `<i data-lucide="${kind === 'success' ? 'circle-check-big' : 'triangle-alert'}"></i><div>${message}</div>`
      ;(window as typeof window & { lucide?: { createIcons?: (options?: unknown) => void } }).lucide?.createIcons?.()
    }

    const handleSubmit = (event: Event) => {
      event.preventDefault()
      event.stopImmediatePropagation()
      if (!form.reportValidity()) return

      const fields = new FormData(form)
      const projectId = projectIdFromForm(form)

      submit.disabled = true
      submit.textContent = 'Enviando solicitud…'
      status.hidden = true

      void client.functions.invoke<AppointmentResponse>('webapp-transactional-email', {
        body: {
          action: 'create_public_appointment',
          companyId: activeTenant.companyId,
          fullName: String(fields.get('full_name') || ''),
          email: String(fields.get('email') || ''),
          phone: String(fields.get('phone') || ''),
          projectId,
          requestedDate: String(fields.get('requested_date') || ''),
          requestedTime: String(fields.get('requested_time') || ''),
          modality: String(fields.get('modality') || 'project_visit'),
          notes: String(fields.get('notes') || ''),
        },
      }).then(({ data, error }) => {
        if (error || !data?.ok) {
          throw new Error(error?.message || data?.error || 'No fue posible registrar la cita.')
        }

        form.reset()
        void availability.refresh()
        setStatus(
          'success',
          'Solicitud recibida. La cita quedó pendiente de confirmación y te enviaremos por correo cada cambio de estado.',
        )
      }).catch((error: unknown) => {
        setStatus(
          'error',
          error instanceof Error ? error.message : 'No fue posible registrar la cita. Inténtalo nuevamente.',
        )
      }).finally(() => {
        submit.disabled = false
        submit.innerHTML = 'Solicitar cita <i data-lucide="arrow-right"></i>'
        ;(window as typeof window & { lucide?: { createIcons?: (options?: unknown) => void } }).lucide?.createIcons?.()
      })
    }

    form.addEventListener('submit', handleSubmit)
    return () => {
      form.removeEventListener('submit', handleSubmit)
      projectSelect?.removeEventListener('change', projectChanged)
      availability.dispose()
      delete form.dataset.appointmentBound
    }
  }, [ready])

  return null
}
